import { useState, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Popper,
  Paper,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  ClickAwayListener,
} from "@mui/material";
import { useSelector } from "react-redux";

// Plain-text comment input with @mention autocomplete. Typing '@' opens a
// member list; selecting a member splices an @[Name](userId) token into the
// body (the format the backend extracts mentions from).
const CommentInput = ({
  onSubmit,
  initialValue = '',
  placeholder = 'Add a comment… use @ to mention',
  submitLabel = 'Comment',
  onCancel,
  autoFocus = false,
}) => {
  const [value, setValue] = useState(initialValue);
  const [submitting, setSubmitting] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null); // null = popup closed
  const inputRef = useRef(null);

  const members = useSelector(
    (state) => state.workspace?.workspaceInfo?.data?.user_details
  ) || [];

  const filteredMembers = mentionQuery !== null
    ? members.filter((m) =>
        (m.name || m.email || '').toLowerCase().includes(mentionQuery.toLowerCase())
      )
    : [];

  const handleChange = (e) => {
    const next = e.target.value;
    setValue(next);

    // Open the mention popup when the text before the caret ends with
    // '@word' preceded by start-of-text or whitespace.
    const caret = e.target.selectionStart;
    const beforeCaret = next.slice(0, caret);
    const match = beforeCaret.match(/(?:^|\s)@([\w]*)$/);
    setMentionQuery(match ? match[1] : null);
  };

  const insertMention = (member) => {
    const input = inputRef.current;
    const caret = input?.selectionStart ?? value.length;
    const beforeCaret = value.slice(0, caret);
    const afterCaret = value.slice(caret);
    const token = `@[${member.name || member.email}](${member._id}) `;
    const replaced = beforeCaret.replace(/(^|\s)@[\w]*$/, `$1${token}`);
    setValue(replaced + afterCaret);
    setMentionQuery(null);
    input?.focus();
  };

  const handleSubmit = async () => {
    const body = value.trim();
    if (!body || submitting) return;
    setSubmitting(true);
    try {
      const ok = await onSubmit(body);
      if (ok !== false) setValue('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ClickAwayListener onClickAway={() => setMentionQuery(null)}>
      <Box>
        <TextField
          inputRef={inputRef}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          multiline
          minRows={2}
          maxRows={6}
          fullWidth
          size="small"
          autoFocus={autoFocus}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSubmit();
            }
            if (e.key === 'Escape') setMentionQuery(null);
          }}
        />
        <Popper
          open={mentionQuery !== null && filteredMembers.length > 0}
          anchorEl={inputRef.current}
          placement="top-start"
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        >
          <Paper elevation={4} sx={{ maxHeight: 220, overflowY: 'auto', width: 280 }}>
            <List dense disablePadding>
              {filteredMembers.map((member) => (
                <ListItemButton key={member._id} onClick={() => insertMention(member)}>
                  <ListItemAvatar sx={{ minWidth: 36 }}>
                    <Avatar src={member.avatarUrl} sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                      {(member.name || member.email || '?').charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={member.name || member.email} secondary={member.name ? member.email : null} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Popper>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
          {onCancel && (
            <Button size="small" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            onClick={handleSubmit}
            disabled={!value.trim() || submitting}
          >
            {submitLabel}
          </Button>
        </Box>
      </Box>
    </ClickAwayListener>
  );
};

export default CommentInput;
